import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Lock } from "lucide-react";
import { PageHero } from "./ContentPage";
import { Btn, Container } from "../components/Shared";
import { useUploads } from "../components/upload/FileDrop";
import { StepService, StepProject, StepFiles, StepContact, StepSubmit } from "../components/upload/Steps";
import { api } from "../lib/api";
import { useLang } from "../lib/lang";
import { useSeo } from "../lib/seo";

const DEFAULT_CFG = { max_files: 10, max_mb: 20, retention_days: 90, extensions: ["pdf", "jpg", "jpeg", "png", "doc", "docx"] };
const EMPTY = { service: "", source_lang: "", target_lang: "", document_type: "", intended_use: "", for_authority: "unsure", deadline: "", delivery: "", instructions: "", name: "", email: "", phone: "", country: "", company: "", contact_pref: "", privacy_ack: false, website: "" };
const token = () => (window.crypto?.randomUUID ? window.crypto.randomUUID().replace(/-/g, "") : `${Date.now()}${Math.random().toString(36).slice(2)}`.padEnd(32, "x"));

export default function Upload({ doc, alternates }) {
  const { t, r, lang } = useLang();
  const u = t.up;
  useSeo({ title: doc.meta_title || `${u.eyebrow} – Translinked`, description: doc.meta_description || u.lede, path: doc.path, lang: doc.lang, alternates });
  const draft = useRef(token());
  const [cfg, setCfg] = useState(DEFAULT_CFG);
  const [step, setStep] = useState(0);
  const [f, setF] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [formErr, setFormErr] = useState("");
  const up = useUploads(draft.current, cfg, u.err);
  const top = useRef(null);
  useEffect(() => { api.get("/config").then((res) => setCfg({ ...DEFAULT_CFG, ...(res.data || {}) })).catch(() => {}); }, []);
  const set = (k, v) => { setF((x) => ({ ...x, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined, privacy: k === "privacy_ack" ? undefined : e.privacy })); };

  const validate = (s) => {
    const e = {};
    const req = u.err.required;
    if (s === 0 && !f.service) e.service = req;
    if (s === 1) { if (!f.source_lang) e.source_lang = req; if (!f.target_lang) e.target_lang = req; if (f.document_type.trim().length < 2) e.document_type = req; }
    if (s === 2) {
      if (up.files.some((x) => x.status === "uploading")) e.files = u.s3.uploading;
      else if (f.service !== "interpreting" && !up.files.some((x) => x.status === "done")) e.files = u.s3.required;
    }
    if (s === 3) { if (f.name.trim().length < 2) e.name = req; if (!/^\S+@\S+\.\S+$/.test(f.email)) e.email = u.err.email; }
    if (s === 4 && !f.privacy_ack) e.privacy = u.err.privacy;
    setErrors(e);
    return !Object.keys(e).length;
  };
  const go = (n) => { setStep(n); top.current?.scrollIntoView({ behavior: "smooth", block: "start" }); };
  const next = () => { if (validate(step)) go(step + 1); };
  const submit = async () => {
    if (!validate(4)) return;
    setSubmitting(true); setFormErr("");
    try {
      const ids = up.files.filter((x) => x.status === "done").map((x) => x.id);
      const res = await api.post("/quote-requests", { ...f, lang, draft_token: draft.current, upload_ids: ids });
      setResult(res.data);
      top.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (e) {
      const d = e?.response?.data?.detail;
      setFormErr(e?.response?.status === 429 ? u.err.rate : (typeof d === "string" && u.err[d]) || u.err.generic);
    } finally { setSubmitting(false); }
  };
  const reset = () => { draft.current = token(); setF(EMPTY); setStep(0); setResult(null); window.location.reload(); };

  const steps = [StepService, StepProject, StepFiles, StepContact, StepSubmit];
  const Step = steps[step];
  return (
    <main data-testid="upload-page">
      <PageHero eyebrow={u.eyebrow} title={u.title} lede={u.lede} />
      <Container className="pb-28">
        <div ref={top} className="scroll-mt-24 grid gap-10 lg:grid-cols-12">
          <aside className="min-w-0 lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <ol className="flex gap-2 overflow-x-auto pb-2 lg:flex-col lg:gap-0 lg:overflow-visible" data-testid="upload-stepper" data-lenis-prevent>
                {u.steps.map((s, i) => (
                  <li key={s} className={`flex shrink-0 items-center gap-3 rounded-full border px-4 py-2 text-sm lg:rounded-none lg:border-0 lg:border-t lg:px-0 lg:py-4 ${i === step && !result ? "border-[var(--ink)] text-[var(--ink)] lg:border-[var(--ink)]" : "border-[var(--line)] text-[var(--muted)]"}`} aria-current={i === step ? "step" : undefined}>
                    <span className={`flex h-7 w-7 items-center justify-center rounded-full font-mono text-xs ${i < step || result ? "bg-[var(--teal)] text-[var(--ink)]" : i === step ? "bg-[var(--ink)] text-white" : "border border-[var(--line)]"}`}>{i < step || result ? "✓" : i + 1}</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ol>
              <div className="mt-6 hidden h-1 overflow-hidden rounded-full bg-[var(--paper-2)] lg:block"><motion.div className="h-full bg-[var(--teal)]" animate={{ width: `${((result ? 5 : step) / 5) * 100}%` }} /></div>
              <p className="mt-8 hidden items-start gap-3 text-sm text-[var(--muted)] lg:flex"><Lock className="mt-0.5 h-4 w-4 shrink-0 text-[var(--teal-ink)]" />{u.notice}</p>
            </div>
          </aside>
          <div className="min-w-0 lg:col-span-8">
            {result ? (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="rounded-sm border border-[var(--line)] bg-white p-8 md:p-12" data-testid="upload-success">
                <CheckCircle2 className="h-12 w-12 text-[var(--teal-ink)]" />
                <h2 className="mt-6 font-display text-3xl font-light text-[var(--ink)] md:text-4xl">{u.ok.title}</h2>
                <p className="mt-6 eyebrow text-[var(--muted)]">{u.ok.ref}</p>
                <p className="mt-2 font-mono text-2xl text-[var(--ink)]" data-testid="upload-reference">{result.reference}</p>
                <p className="mt-6 text-[var(--muted)]">{u.ok.text}</p>
                {!result.confirmation_email && <p className="mt-3 text-sm text-[#8A5A00]">{u.ok.noMail}</p>}
                <p className="mt-3 text-sm text-[var(--ink)]">{u.ok.text2}</p>
                <div className="mt-8 flex flex-wrap gap-3"><Btn to={r("home")} data-testid="upload-success-home">{u.ok.home}</Btn><Btn variant="ghost" icon={null} onClick={reset} data-testid="upload-another">{u.ok.another}</Btn></div>
              </motion.div>
            ) : (
              <div className="rounded-sm border border-[var(--line)] bg-[var(--paper)] p-6 md:p-10">
                <p className="mb-6 font-mono text-xs uppercase tracking-wider text-[var(--muted)]">{u.step} {step + 1} / 5</p>
                <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}>
                  <Step u={u} f={f} set={set} errors={errors} cfg={cfg} up={up} lang={lang} privacyPath={r("privacy")} />
                </motion.div>
                {formErr && <p className="mt-6 rounded-sm bg-[#FDECEA] px-4 py-3 text-sm text-[#B42318]" role="alert" data-testid="upload-form-error">{formErr}</p>}
                <div className="mt-10 flex items-center justify-between gap-4 border-t border-[var(--line)] pt-6">
                  {step > 0 ? <button type="button" onClick={() => go(step - 1)} className="text-sm text-[var(--ink)] underline underline-offset-4" data-testid="upload-back-btn">{u.back}</button> : <span />}
                  {step < 4 ? (
                    <Btn type="button" onClick={next} data-testid="upload-next-btn">{u.next}</Btn>
                  ) : (
                    <Btn type="button" onClick={submit} disabled={submitting} icon={submitting ? null : "arrow"} className="text-left" data-testid="upload-submit-btn">
                      {submitting ? <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />{u.s5.submitting}</span> : u.s5.submit}
                    </Btn>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </Container>
    </main>
  );
}
